// The table's own quick filter, order and paging, pure. The chips over "All
// blunders" / "All misses" narrow the rows in place: chips of one group OR
// together, groups AND; each chip counts the rows it would add under the
// other groups' active chips. The Pattern group lists the motif families
// (Pin, Fork, Mate …); a family chosen reveals its variations as a second
// row ("Pin: against the king 4 · against the queen 7"), each a chip that
// narrows that family only: a row passes when its family is chosen and,
// when any variation of its family is chosen, its own is among them. The
// state lives in the address (`?severity=decisive&motif=pin,fork&variation=pin/queen&order=newest&all=1`),
// so a filtered view can be shared and comes back with Back from the
// review; the groups' query keys are the `mistake` criterion's dimension
// names (plus `stage` and `variation`, the motif's second half).
import { MISTAKE_STANDINGS, type MoveKind, type Phase } from '@chess/api-model';
import {
  CLOCK_BUCKETS,
  familyLabel,
  isMotifFamily,
  MOTIF_FAMILIES,
  MOTIF_VARIATIONS,
  motifId,
  parseMotifId,
  SEVERITIES,
  SEVERITY_LABEL,
  STAGE_LABEL,
  STAGES,
  variationLabel,
  type MotifFamily,
} from '@chess/api-mistakes';
import { CLOCK_SHORT, STANDING_SHORT } from './cards.ts';
import type { MistakeRow } from './MistakesAggregator.ts';

/** The chips' groups, in the order they show; `variation` is the row under `motif`, shown for the families chosen. */
export type ChipGroup = 'severity' | 'motif' | 'variation' | 'stage' | 'standing' | 'clock';
export const CHIP_GROUPS = ['severity', 'motif', 'variation', 'stage', 'standing', 'clock'] as const satisfies readonly ChipGroup[];

/** Every variation of every family, as `family/variation`, in the scheme's order. */
export const VARIATION_IDS: readonly string[] = MOTIF_FAMILIES.flatMap((f) => MOTIF_VARIATIONS[f].map((v) => `${f}/${v}`));

/** Each group's values, in the order its chips show. */
export const CHIP_VALUES: Record<ChipGroup, readonly string[]> = {
  severity: SEVERITIES,
  motif: MOTIF_FAMILIES,
  variation: VARIATION_IDS,
  stage: STAGES,
  standing: MISTAKE_STANDINGS,
  clock: CLOCK_BUCKETS,
};

export const CHIP_GROUP_LABEL: Record<ChipGroup, string> = { severity: 'Severity', motif: 'Pattern', variation: 'Variation', stage: 'Stage', standing: 'Where you stood', clock: 'Clock' };

/** A chip's words, within its kind: a family's name, a variation's in words after its family ("against the queen"). */
export function chipLabel(kind: MoveKind, group: ChipGroup, value: string): string {
  switch (group) {
    case 'severity':
      return SEVERITY_LABEL[value as keyof typeof SEVERITY_LABEL];
    case 'motif':
      return isMotifFamily(value) ? familyLabel(value) : value;
    case 'variation': {
      const motif = parseMotifId(value);
      return (motif?.variation && variationLabel(motif.family, motif.variation)) || value;
    }
    case 'stage':
      return STAGE_LABEL[value as Phase];
    case 'standing':
      return STANDING_SHORT[value as keyof typeof STANDING_SHORT];
    case 'clock':
      return CLOCK_SHORT[value as keyof typeof CLOCK_SHORT];
  }
}

/** The family a variation id belongs to. */
export const familyOfVariation = (id: string): MotifFamily | undefined => parseMotifId(id)?.family;

/** The families chosen that have variations, in the scheme's order: the variation rows to show, each with its variation ids. */
export function variationRows(filter: ChipFilter): Array<{ family: MotifFamily; label: string; values: readonly string[] }> {
  return MOTIF_FAMILIES.filter((f) => filter.motif?.includes(f) && MOTIF_VARIATIONS[f].length).map((family) => ({
    family,
    label: familyLabel(family),
    values: MOTIF_VARIATIONS[family].map((v) => `${family}/${v}`),
  }));
}

/** The values chosen in each group; a group absent or empty filters nothing. A variation is only ever chosen with its family. */
export type ChipFilter = Partial<Record<ChipGroup, readonly string[]>>;
/** Costliest first (the default) or newest first. */
export type TableOrder = 'cost' | 'newest';

export interface TableState {
  filter: ChipFilter;
  order: TableOrder;
  /** Every row, not only the first page. */
  all: boolean;
}

/** Rows shown before "Show all". */
export const PAGE_ROWS = 25;

/** A row's value in a group; undefined when the row has none (no clock, no standing kept, a motif without a variation). */
export function rowValue(row: MistakeRow, group: ChipGroup): string | undefined {
  switch (group) {
    case 'motif':
      return row.motif.family;
    case 'variation':
      return row.motif.variation ? motifId(row.motif) : undefined;
    default:
      return row[group];
  }
}

const active = (filter: ChipFilter, group: ChipGroup) => (filter[group]?.length ? filter[group] : undefined);

/** The motif and variation groups are one filter: skipping either skips both. */
const skips = (except: ChipGroup | undefined, group: ChipGroup) =>
  group === except || ((except === 'motif' || except === 'variation') && (group === 'motif' || group === 'variation'));

/** Whether a row passes every group's chips, `except` one group (to count that group's chips). */
export function matches(row: MistakeRow, filter: ChipFilter, except?: ChipGroup): boolean {
  return CHIP_GROUPS.every((g) => {
    if (skips(except, g)) return true;
    const values = active(filter, g);
    if (!values) return true;
    const v = rowValue(row, g);
    if (g === 'variation') {
      // Only the chosen variations of the row's own family narrow it.
      const own = values.filter((id) => familyOfVariation(id) === row.motif.family);
      return !own.length || (v !== undefined && own.includes(v));
    }
    return v !== undefined && values.includes(v);
  });
}

export const filterRows = (rows: readonly MistakeRow[], filter: ChipFilter): MistakeRow[] => rows.filter((r) => matches(r, filter));

export const isFiltered = (filter: ChipFilter): boolean => CHIP_GROUPS.some((g) => !!active(filter, g));

/** Each chip's count: the rows with its value that pass the other groups' chips (a family's or a variation's: the rows passing every group but the motif's). */
export function chipCounts(rows: readonly MistakeRow[], filter: ChipFilter): Record<ChipGroup, Record<string, number>> {
  const out = Object.fromEntries(CHIP_GROUPS.map((g) => [g, Object.fromEntries(CHIP_VALUES[g].map((v) => [v, 0]))])) as Record<ChipGroup, Record<string, number>>;
  for (const row of rows)
    for (const g of CHIP_GROUPS) {
      const v = rowValue(row, g);
      if (v !== undefined && v in out[g] && matches(row, filter, g)) out[g][v] += 1;
    }
  return out;
}

/** The values of a group in the chips' order, keeping those chosen. */
const ordered = (group: ChipGroup, chosen: readonly string[]) => CHIP_VALUES[group].filter((v) => chosen.includes(v));

/** A filter with its variations limited to the families chosen, and no empty group. */
function tidy(filter: ChipFilter): ChipFilter {
  const out: ChipFilter = {};
  for (const g of CHIP_GROUPS) {
    let values = filter[g] ?? [];
    if (g === 'variation') values = values.filter((id) => filter.motif?.includes(familyOfVariation(id) ?? ''));
    if (values.length) out[g] = values;
  }
  return out;
}

/**
 * The chip turned on or off; the other groups unchanged. A family turned
 * off drops its variations; a variation turned on turns its family on.
 */
export function toggleChip(filter: ChipFilter, group: ChipGroup, value: string): ChipFilter {
  const now = filter[group] ?? [];
  const next = now.includes(value) ? now.filter((v) => v !== value) : ordered(group, [...now, value]);
  const out: ChipFilter = { ...filter, [group]: next };
  if (group === 'variation' && next.includes(value)) {
    const family = familyOfVariation(value);
    if (family && !filter.motif?.includes(family)) out.motif = ordered('motif', [...(filter.motif ?? []), family]);
  }
  return tidy(out);
}

/** Costliest first (then newest, then by move), or newest first (then by move). */
export function sortRows(rows: readonly MistakeRow[], order: TableOrder): MistakeRow[] {
  const newest = (a: MistakeRow, b: MistakeRow) => b.playedAt - a.playedAt || a.ply - b.ply;
  return [...rows].sort(order === 'cost' ? (a, b) => b.cost - a.cost || newest(a, b) : newest);
}

/** The rows to draw: the first page, or every row with `all`. */
export const pageRows = (rows: readonly MistakeRow[], all: boolean): readonly MistakeRow[] => (all ? rows : rows.slice(0, PAGE_ROWS));

/** What the table shows for a state: the rows passing the chips, sorted, paged; how many pass; the chips' counts. */
export function tableView(rows: readonly MistakeRow[], state: TableState) {
  const filtered = sortRows(filterRows(rows, state.filter), state.order);
  return { rows: pageRows(filtered, state.all), matching: filtered.length, total: rows.length, counts: chipCounts(rows, state.filter), filtered: isFiltered(state.filter) };
}

/** The state in an address's query; unknown values, and variations of a family not chosen, are dropped. */
export function parseTableState(query: URLSearchParams): TableState {
  const filter: ChipFilter = {};
  for (const g of CHIP_GROUPS) {
    const chosen = query.getAll(g).flatMap((v) => v.split(','));
    const values = ordered(g, chosen);
    if (values.length) filter[g] = values;
  }
  return { filter: tidy(filter), order: query.get('order') === 'newest' ? 'newest' : 'cost', all: query.get('all') === '1' };
}

/** The address's query with the state written in (comma-joined values); other parameters kept, defaults left out. */
export function writeTableState(query: URLSearchParams, state: TableState): URLSearchParams {
  const out = new URLSearchParams(query);
  for (const g of CHIP_GROUPS) {
    out.delete(g);
    const values = state.filter[g];
    if (values?.length) out.set(g, values.join(','));
  }
  out.delete('order');
  if (state.order === 'newest') out.set('order', 'newest');
  out.delete('all');
  if (state.all) out.set('all', '1');
  return out;
}
